import React from "react";

/**
 * Top-level error boundary so a component crash never produces a blank white page.
 * Shows the actual error + stack so production bugs are debuggable from the user's screen.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    try {
      // Bust the cache hard
      if ("caches" in window) {
        caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
        });
      }
    } catch {
      // ignore
    }
    window.location.reload(true);
  };

  render() {
    if (this.state.error) {
      const errMsg = this.state.error?.message || String(this.state.error);
      const stack = this.state.error?.stack || this.state.info?.componentStack || "";
      return (
        <div
          data-testid="error-boundary"
          className="min-h-screen bg-[#0b0d18] text-zinc-200 p-6 font-sans"
        >
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="text-rose-400 text-sm uppercase tracking-wider">
              Page crashed
            </div>
            <h1 className="text-2xl font-bold">Something broke on this screen</h1>
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm">
              <div className="font-mono whitespace-pre-wrap break-words text-rose-200">
                {errMsg}
              </div>
            </div>
            {stack && (
              <details className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-4 text-xs">
                <summary className="cursor-pointer text-zinc-400">
                  Stack trace (click to expand)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-words text-zinc-500">
                  {stack}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm"
              >
                Reload &amp; clear cache
              </button>
              <button
                data-testid="error-boundary-home"
                onClick={() => (window.location.href = "/")}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm"
              >
                Go home
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 pt-4">
              Build: {process.env.REACT_APP_BUILD_ID || "dev"} · Time:{" "}
              {new Date().toISOString()}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
